import { useRef, useCallback } from 'react';
import type { Editor } from '@tiptap/react';

export function useImageUpload(
  editor: Editor | null,
  onImageUpload?: (file: File) => Promise<string>
) {
  const imageInputRef = useRef<HTMLInputElement>(null);

  const handleImageUpload = useCallback(
    async (file: File) => {
      if (!editor) return;

      if (onImageUpload) {
        try {
          const url = await onImageUpload(file);
          const alt = window.prompt('Enter alt text for this image (optional):') || '';
          editor.chain().focus().setImage({ src: url, alt }).run();
        } catch (error) {
          console.error('Failed to upload image:', error);
        }
      } else {
        // Fallback: use base64 encoding
        const reader = new FileReader();
        reader.onload = () => {
          const base64 = reader.result as string;
          const alt = window.prompt('Enter alt text for this image (optional):') || '';
          editor.chain().focus().setImage({ src: base64, alt }).run();
        };
        reader.readAsDataURL(file);
      }
    },
    [editor, onImageUpload]
  );

  const handleImageInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        handleImageUpload(file);
      }
      // Reset input
      if (imageInputRef.current) {
        imageInputRef.current.value = '';
      }
    },
    [handleImageUpload]
  );

  const triggerImageUpload = useCallback(() => {
    imageInputRef.current?.click();
  }, []);

  return {
    imageInputRef,
    handleImageInputChange,
    triggerImageUpload,
  };
}
